import {
  getControlTableData,
  getGraphsData,
  unlockVolSurfaceStickyStrikes,
} from "api/volSurfaceApi";
import { VolSurfaceContext } from "contexts/VolSurfaceContext";
import { useVolSurfaceControlTable } from "contexts/VolSurfaceControlTableContext";
import { getDataFromLocalStorage } from "helpers/generalHelper";
import { storeInLocalStorage } from "helpers/Pricer/Table";
import { checkForApoShortcutName } from "helpers/volSurfaceHelper";
import useVolSurfaceWebSocket from "hooks/useVolSurfaceWebSocket";
import { useContext, useEffect, useRef, useState } from "react";

function useControlTable({ start_date, end_date, setComparisonSurfaceId }) {
  const [isLocking, setIsLocking] = useState(false);
  const { setVolSurfaceData, setVolSurfaceBtnLoading, volSurfaceBtnLoading } =
    useContext(VolSurfaceContext);

  const {
    setVolSurfaceData: setRowsData,
    selectedFilters,
    setVolCalcTable,
    setRiskFree,
    setCostOfCarry,
    setAverage,
    setLockedData,
    lockedData,
    setMarksCalcDate,
    setCanChangeMarksCalcDate,
  } = useVolSurfaceControlTable();

  // Add ref to prevent multiple simultaneous calls
  const isRunningRef = useRef(false);
  const timeoutRef = useRef(null);
  const prevComparisonSurfaceIdRef = useRef(null);
  const debounceRef = useRef();

  function getTableDate({ checkPopup = true }) {
    if (!selectedFilters?.product_id || isRunningRef.current) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the call
    timeoutRef.current = setTimeout(() => {
      isRunningRef.current = true;
      setVolSurfaceBtnLoading(true);

      // Check if only comparison_surface_id changed
      const isOnlyComparisonSurfaceChanged =
        prevComparisonSurfaceIdRef.current !== null &&
        prevComparisonSurfaceIdRef.current !==
          selectedFilters?.comparison_surface_id;

      const isComparisonSurfaceChanged = getDataFromLocalStorage(
        "IsComparisonSurfaceChanged"
      );

      const isMarksCalcDateChanged = getDataFromLocalStorage(
        "IsMarksCalcDateChanged"
      );

      let queryParams = {
        product_contract_style_id: selectedFilters?.product_id,
      };

      if (isMarksCalcDateChanged) {
        queryParams.calculation_date = selectedFilters?.calculation_date;
      }

      if (isComparisonSurfaceChanged) {
        queryParams.comparison_surface_id =
          selectedFilters?.comparison_surface_id;
      }

      if (selectedFilters?.company_id) {
        queryParams.company_id = selectedFilters?.company_id;
      }

      if (start_date && end_date) {
        queryParams.start_date = start_date;
        queryParams.end_date = end_date;
      }
      // Only include risk_free if it changed or if it's the first load
      const riskFreeChanged = getDataFromLocalStorage("IsRiskFreeChanged");
      if (riskFreeChanged) {
        queryParams.risk_free = selectedFilters?.risk_free;
      }

      // Only include average and cost_of_carry if not only comparison_surface_id changed
      if (
        checkForApoShortcutName(selectedFilters) &&
        !isOnlyComparisonSurfaceChanged
      ) {
        const averageAndCostOfCarryIsReloaded = getDataFromLocalStorage(
          "AverageAndCostOfCarryIsReloaded"
        );

        if (
          !averageAndCostOfCarryIsReloaded &&
          selectedFilters?.average !== null &&
          selectedFilters?.cost_of_carry !== null
        ) {
          queryParams.average = selectedFilters?.average;
          queryParams.cost_of_carry = selectedFilters?.cost_of_carry;
        }
      }

      // Update the previous values
      prevComparisonSurfaceIdRef.current =
        selectedFilters?.comparison_surface_id;

      getControlTableData(queryParams)
        .then((res) => {
          const data = res.data.data;
          setRowsData(data.main_control_table);
          setVolCalcTable(data.vol_calc_table);
          setComparisonSurfaceId(data?.comparison_surface_id);
          setRiskFree(
            data?.risk_free !== undefined && data?.risk_free !== null
              ? Number(data?.risk_free)
              : null
          );
          setAverage(
            data?.average !== undefined && data?.average !== null
              ? Number(data?.average)
              : null
          );
          setCostOfCarry(
            data?.cost_of_carry !== undefined && data?.cost_of_carry !== null
              ? Number(data?.cost_of_carry)
              : null
          );
          setMarksCalcDate(data?.calculation_date);
          setCanChangeMarksCalcDate(data?.can_change_calculation_date);
          storeInLocalStorage("IsMarksCalcDateChanged", JSON.stringify(false));
          if (checkPopup) {
            setLockedData({
              model_id: data?.model_id,
              model: data?.model,
              show_lock_modal: data?.is_surface_locked,
              is_locked: data?.is_surface_locked,
              locked_by: data?.locked_by,
              lock_comparison_surface_dropdown:
                data?.lock_comparison_surface_dropdown,
              event: null,
            });
          }
        })
        .catch((err) => {
          setVolSurfaceData(null);
          setRowsData(null);
          setVolCalcTable(null);
          setComparisonSurfaceId(null);
        })
        .finally(() => {
          setVolSurfaceBtnLoading(false);
          isRunningRef.current = false;
        });
    }, 50); // 50ms debounce
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function updateAndTakeOverLock() {
    setIsLocking(true);
    unlockVolSurfaceStickyStrikes({
      model_id: lockedData?.model_id,
      model: lockedData?.model,
    })
      .then((res) => {
        if (res.status === 200) {
          setLockedData({
            model_id: null,
            model: null,
            show_lock_modal: false,
            is_locked: false,
            locked_by: "",
            lock_comparison_surface_dropdown:
              lockedData?.lock_comparison_surface_dropdown,
            event: null,
          });
          setIsLocking(false);
          getTableDate({ checkPopup: false });
          storeInLocalStorage("checkPopup", JSON.stringify(true));
        }
      })
      .catch((e) => {
        setIsLocking(false);
      });
  }

  useEffect(() => {
    if (!selectedFilters?.product_id || volSurfaceBtnLoading) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const checkPopup = getDataFromLocalStorage("checkPopup");
      if (checkPopup) {
        getTableDate({ checkPopup: true });
      }
    }, 150); // debounce delay – outer debounce to prevent multiple calls

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters?.comparison_surface_id,
    selectedFilters?.product_id,
    selectedFilters?.risk_free,
    selectedFilters?.average,
    selectedFilters?.cost_of_carry,
    selectedFilters?.company_id,
    selectedFilters?.calculation_date,
    selectedFilters?.live_surface_id,
    start_date,
    end_date,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      storeInLocalStorage("checkPopup", JSON.stringify(true));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  //websocket call for control table
  useVolSurfaceWebSocket(selectedFilters, () => {
    if (!selectedFilters?.product_id) return;
    storeInLocalStorage("checkPopup", JSON.stringify(false));
    storeInLocalStorage("IsComparisonSurfaceChanged", JSON.stringify(false));
    storeInLocalStorage("IsRiskFreeChanged", JSON.stringify(false));
    storeInLocalStorage("IsMarksCalcDateChanged", JSON.stringify(false));
    storeInLocalStorage(
      "AverageAndCostOfCarryIsReloaded",
      JSON.stringify(true)
    );
    getTableDate({ checkPopup: false });
    const isComparisonSurfaceChanged = getDataFromLocalStorage(
      "IsComparisonSurfaceChanged"
    );
    let queryParams = {
      product_contract_style_id: selectedFilters?.product_id,
      start_date: start_date,
      end_date: end_date,
    };
    if (isComparisonSurfaceChanged) {
      queryParams.comparison_surface_id =
        selectedFilters?.comparison_surface_id;
    }
    getGraphsData(queryParams).then((res) => {
      const response = res.data.data;
      setVolSurfaceData(response);
    });
  });

  return {
    isLocking,
    updateAndTakeOverLock,
  };
}

export default useControlTable;
